import api from './itemsApiClient';

const base = '/customizations';

const unwrapResults = (data) => {
  const results = Array.isArray(data) ? data : (data?.results || []);
  return {
    count: Array.isArray(data) ? data.length : Number(data?.count || results.length),
    results,
  };
};

export const CUSTOMIZATION_INBOX_FILTERS = Object.freeze({
  needs_response: ['sent'],
  active: ['quoted', 'accepted'],
  closed: ['converted', 'cancelled', 'expired'],
});

export const customizationBelongsToInboxFilter = (state, filter) =>
  (CUSTOMIZATION_INBOX_FILTERS[filter] || []).includes(state);

export const getCustomizationRequestId = (negotiation) => {
  const value = negotiation?.customization_request_id ?? negotiation?.customization_request;
  return value && typeof value === 'object' ? (value.id ?? null) : (value ?? null);
};

export const customizationsAPI = {
  createDraft: async (payload = {}) => {
    const response = await api.post(`${base}/requests/`, payload);
    return response.data;
  },

  getDraft: async (kind = 'painting') => {
    try {
      const response = await api.get(`${base}/requests/draft/`, { params: { kind } });
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  listCustomBoutiques: async (params = {}) => {
    const response = await api.get(`${base}/boutiques/`, { params });
    return unwrapResults(response.data);
  },

  listBoutiqueOfferingsPublic: async (shopId) => {
    const response = await api.get(`${base}/boutiques/${shopId}/offerings/`);
    return response.data;
  },

  createBoutiqueDraft: async (payload) => {
    const response = await api.post(`${base}/requests/`, { kind: 'boutique', ...payload });
    return response.data;
  },

  listBuyerRequests: async () => {
    const response = await api.get(`${base}/requests/`);
    return unwrapResults(response.data);
  },

  getBuyerRequest: async (requestId) => {
    const response = await api.get(`${base}/requests/${requestId}/`);
    return response.data;
  },

  updateDraft: async (requestId, payload) => {
    const response = await api.patch(`${base}/requests/${requestId}/`, payload);
    return response.data;
  },

  uploadReferences: async (requestId, files) => {
    const data = new FormData();
    files.forEach((file) => data.append('images', file));
    const response = await api.post(
      `${base}/requests/${requestId}/references/`,
      data,
      { headers: { 'Content-Type': undefined }, timeout: 120000 },
    );
    return response.data;
  },

  deleteReference: async (requestId, referenceId) => {
    await api.delete(`${base}/requests/${requestId}/references/${referenceId}/`);
  },

  submitDraft: async (requestId) => {
    const response = await api.post(`${base}/requests/${requestId}/submit/`, {});
    return response.data;
  },

  getRecommendations: async (requestId) => {
    const response = await api.get(`${base}/requests/${requestId}/recommendations/`);
    return response.data;
  },

  sendBuyerRequest: async (requestId, shopId) => {
    const body = shopId == null ? {} : { shop_id: shopId };
    const response = await api.post(`${base}/requests/${requestId}/send/`, body);
    return response.data;
  },

  acceptQuote: async (requestId) => {
    const response = await api.post(`${base}/requests/${requestId}/quote/accept/`, {});
    return response.data;
  },

  declineQuote: async (requestId) => {
    const response = await api.post(`${base}/requests/${requestId}/quote/decline/`, {});
    return response.data;
  },

  cancelBuyerRequest: async (requestId) => {
    const response = await api.post(`${base}/requests/${requestId}/cancel/`, {});
    return response.data;
  },

  getArtistInbox: async () => {
    const response = await api.get(`${base}/artist/requests/`);
    return unwrapResults(response.data);
  },

  getArtistRequest: async (requestId) => {
    const response = await api.get(`${base}/artist/requests/${requestId}/`);
    return response.data;
  },

  declineArtistRequest: async (requestId) => {
    const response = await api.post(`${base}/artist/requests/${requestId}/decline/`, {});
    return response.data;
  },

  sendArtistQuote: async (requestId, payload) => {
    const response = await api.post(`${base}/artist/requests/${requestId}/quote/`, payload);
    return response.data;
  },

  getArtistSettings: async () => {
    const response = await api.get(`${base}/artist/settings/`);
    return response.data;
  },

  updateArtistSettings: async (payload) => {
    const response = await api.patch(`${base}/artist/settings/`, payload);
    return response.data;
  },

  getBoutiqueSettings: async () => {
    const response = await api.get(`${base}/boutique/settings/`);
    return response.data;
  },

  updateBoutiqueSettings: async (payload) => {
    const response = await api.patch(`${base}/boutique/settings/`, payload);
    return response.data;
  },

  listBoutiqueOfferings: async () => {
    const response = await api.get(`${base}/boutique/offerings/`);
    return unwrapResults(response.data);
  },

  createBoutiqueOffering: async (payload) => {
    const response = await api.post(`${base}/boutique/offerings/`, payload);
    return response.data;
  },

  updateBoutiqueOffering: async (offeringId, payload) => {
    const response = await api.patch(`${base}/boutique/offerings/${offeringId}/`, payload);
    return response.data;
  },

  deleteBoutiqueOffering: async (offeringId) => {
    await api.delete(`${base}/boutique/offerings/${offeringId}/`);
  },

  getBoutiqueInbox: async () => {
    const response = await api.get(`${base}/boutique/requests/`);
    return unwrapResults(response.data);
  },

  getBoutiqueRequest: async (requestId) => {
    const response = await api.get(`${base}/boutique/requests/${requestId}/`);
    return response.data;
  },

  declineBoutiqueRequest: async (requestId) => {
    const response = await api.post(`${base}/boutique/requests/${requestId}/decline/`, {});
    return response.data;
  },

  sendBoutiqueQuote: async (requestId, payload) => {
    const response = await api.post(`${base}/boutique/requests/${requestId}/quote/`, payload);
    return response.data;
  },

  listCustomDesigners: async (params = {}) => {
    const response = await api.get(`${base}/designers/`, { params });
    return unwrapResults(response.data);
  },

  listDesignerOfferingsPublic: async (shopId) => {
    const response = await api.get(`${base}/designers/${shopId}/offerings/`);
    return response.data;
  },

  createDesignerDraft: async (payload) => {
    const response = await api.post(`${base}/requests/`, { kind: 'designer', ...payload });
    return response.data;
  },

  getDesignerSettings: async () => {
    const response = await api.get(`${base}/designer/settings/`);
    return response.data;
  },

  updateDesignerSettings: async (payload) => {
    const response = await api.patch(`${base}/designer/settings/`, payload);
    return response.data;
  },

  listDesignerOfferings: async () => {
    const response = await api.get(`${base}/designer/offerings/`);
    return unwrapResults(response.data);
  },

  createDesignerOffering: async (payload) => {
    const response = await api.post(`${base}/designer/offerings/`, payload);
    return response.data;
  },

  updateDesignerOffering: async (offeringId, payload) => {
    const response = await api.patch(`${base}/designer/offerings/${offeringId}/`, payload);
    return response.data;
  },

  deleteDesignerOffering: async (offeringId) => {
    await api.delete(`${base}/designer/offerings/${offeringId}/`);
  },

  getDesignerInbox: async () => {
    const response = await api.get(`${base}/designer/requests/`);
    return unwrapResults(response.data);
  },

  getDesignerRequest: async (requestId) => {
    const response = await api.get(`${base}/designer/requests/${requestId}/`);
    return response.data;
  },

  declineDesignerRequest: async (requestId) => {
    const response = await api.post(`${base}/designer/requests/${requestId}/decline/`, {});
    return response.data;
  },

  sendDesignerQuote: async (requestId, payload) => {
    const response = await api.post(`${base}/designer/requests/${requestId}/quote/`, payload);
    return response.data;
  },
};

export const formatCustomizationMoney = (cents) => {
  if (cents == null || cents === '') return '—';
  return `${(Number(cents) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} EGP`;
};

export const customizationBuyerName = (request) =>
  request?.buyer_display_name
  || request?.buyer?.display_name
  || request?.buyer?.username
  || request?.buyer_name
  || 'Buyer';

export default customizationsAPI;
